import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { get } from 'lodash';
import {
  Accordion,
  AccordionSet,
  FilterAccordionHeader,
} from '@folio/stripes/components';
import {
  CheckboxFilter,
  MultiSelectionFilter,
} from '@folio/stripes/smart-components';
import { getLocalizedValue } from '../../../../../helpers/localizationUtils';

const ACCORDION_ID_PREFIX = 'reports-filter-accordion';

function Filters({ activeFilters = {}, resources, onChangeHandlers, resultOffset }) {
  const intl = useIntl();

  const handleFilterChange = (filter) => {
    if (resultOffset) resultOffset.replace(0);
    onChangeHandlers.state({
      ...activeFilters,
      [filter.name]: filter.values,
    });
  };

  const handleClearGroup = (name) => {
    if (resultOffset) resultOffset.replace(0);
    onChangeHandlers.clearGroup(name);
  };

  const {
    status = [],
    builderType = [],
    visibility = [],
    complexity = [],
    categoryId = [],
    reportType = [],
  } = activeFilters;

  const statusOptions = [
    { value: 'draft', label: intl.formatMessage({ id: 'ui-reports.filters.status.draft', defaultMessage: 'Draft' }) },
    { value: 'published', label: intl.formatMessage({ id: 'ui-reports.filters.status.published', defaultMessage: 'Published' }) },
    { value: 'archived', label: intl.formatMessage({ id: 'ui-reports.filters.status.archived', defaultMessage: 'Archived' }) },
    { value: 'scheduled', label: intl.formatMessage({ id: 'ui-reports.filters.status.scheduled', defaultMessage: 'Scheduled' }) },
  ];

  const builderTypeOptions = [
    { value: 'cataloging', label: intl.formatMessage({ id: 'ui-reports.filters.builderType.cataloging', defaultMessage: 'Cataloging' }) },
    { value: 'circulation', label: intl.formatMessage({ id: 'ui-reports.filters.builderType.circulation', defaultMessage: 'Circulation' }) },
    { value: 'productivity', label: intl.formatMessage({ id: 'ui-reports.filters.builderType.productivity', defaultMessage: 'Productivity' }) },
    { value: 'advanced', label: intl.formatMessage({ id: 'ui-reports.filters.builderType.advanced', defaultMessage: 'Advanced' }) },
  ];

  const visibilityOptions = [
    { value: 'public', label: intl.formatMessage({ id: 'ui-reports.filters.privacyType.public', defaultMessage: 'Public' }) },
    { value: 'private', label: intl.formatMessage({ id: 'ui-reports.filters.privacyType.private', defaultMessage: 'Private' }) },
    { value: 'restricted', label: intl.formatMessage({ id: 'ui-reports.filters.privacyType.restricted', defaultMessage: 'Restricted' }) },
  ];

  const activeOptions = [
    { value: 'true', label: intl.formatMessage({ id: 'ui-reports.filters.active.active', defaultMessage: 'Active' }) },
    { value: 'false', label: intl.formatMessage({ id: 'ui-reports.filters.active.inactive', defaultMessage: 'Inactive' }) },
  ];

  const complexityOptions = [
    { value: 'low',       label: intl.formatMessage({ id: 'ui-reports.performance.complexity.low',       defaultMessage: 'Low' }) },
    { value: 'medium',    label: intl.formatMessage({ id: 'ui-reports.performance.complexity.medium',    defaultMessage: 'Medium' }) },
    { value: 'high',      label: intl.formatMessage({ id: 'ui-reports.performance.complexity.high',      defaultMessage: 'High' }) },
    { value: 'very_high', label: intl.formatMessage({ id: 'ui-reports.performance.complexity.very_high', defaultMessage: 'Very High' }) },
  ];

  const categoryOptions = get(resources, 'categories.records', [])
    .map(c => ({
      value: c.id,
      label: intl.formatMessage({
        id: `ui-reports.categories.name.${c.name}`,
        defaultMessage: c.name || '',
      }),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const reportTypeOptions = get(resources, 'reportTypes.records', [])
    .map(rt => ({
      value: rt.id,
      label: intl.formatMessage({
        id: `ui-reports.reportTypes.name.${rt.code}`,
        defaultMessage: rt.name || '',
      }),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <AccordionSet>
      {/* Status */}
      <Accordion
        displayClearButton={status.length > 0}
        id={`${ACCORDION_ID_PREFIX}-status`}
        header={FilterAccordionHeader}
        label={<FormattedMessage id="ui-reports.filters.status" defaultMessage="Status" />}
        separator={false}
        onClearFilter={() => handleClearGroup('status')}
      >
        <CheckboxFilter
          dataOptions={statusOptions}
          name="status"
          selectedValues={status}
          onChange={handleFilterChange}
        />
      </Accordion>

      {/* Builder Type */}
      <Accordion
        displayClearButton={builderType.length > 0}
        id={`${ACCORDION_ID_PREFIX}-builderType`}
        header={FilterAccordionHeader}
        label={<FormattedMessage id="ui-reports.filters.builderType" defaultMessage="Report Builder" />}
        separator={false}
        onClearFilter={() => handleClearGroup('builderType')}
      >
        <CheckboxFilter
          dataOptions={builderTypeOptions}
          name="builderType"
          selectedValues={builderType}
          onChange={handleFilterChange}
        />
      </Accordion>

      {/* Visibility */}
      <Accordion
        displayClearButton={visibility.length > 0}
        id={`${ACCORDION_ID_PREFIX}-visibility`}
        header={FilterAccordionHeader}
        label={<FormattedMessage id="ui-reports.filters.visibility" defaultMessage="Visibility" />}
        separator={false}
        onClearFilter={() => handleClearGroup('visibility')}
      >
        <CheckboxFilter
          dataOptions={visibilityOptions}
          name="visibility"
          selectedValues={visibility}
          onChange={handleFilterChange}
        />
      </Accordion>

      {/* Complexity */}
      <Accordion
        displayClearButton={complexity.length > 0}
        id={`${ACCORDION_ID_PREFIX}-complexity`}
        header={FilterAccordionHeader}
        label={<FormattedMessage id="ui-reports.filters.complexity" defaultMessage="Complexity" />}
        separator={false}
        onClearFilter={() => handleClearGroup('complexity')}
      >
        <CheckboxFilter
          dataOptions={complexityOptions}
          name="complexity"
          selectedValues={complexity}
          onChange={handleFilterChange}
        />
      </Accordion>

      {/* Category */}
      {categoryOptions.length > 0 && (
        <Accordion
          displayClearButton={categoryId.length > 0}
          id={`${ACCORDION_ID_PREFIX}-categoryId`}
          header={FilterAccordionHeader}
          label={<FormattedMessage id="ui-reports.filters.category" defaultMessage="Category" />}
          separator={false}
          onClearFilter={() => handleClearGroup('categoryId')}
        >
          <MultiSelectionFilter
            id="category-filter"
            dataOptions={categoryOptions}
            name="categoryId"
            selectedValues={categoryId}
            onChange={handleFilterChange}
          />
        </Accordion>
      )}

      {/* Report Type */}
      {reportTypeOptions.length > 0 && (
        <Accordion
          displayClearButton={reportType.length > 0}
          id={`${ACCORDION_ID_PREFIX}-reportType`}
          header={FilterAccordionHeader}
          label={<FormattedMessage id="ui-reports.filters.reportType" defaultMessage="Report Type" />}
          separator={false}
          onClearFilter={() => handleClearGroup('reportType')}
        >
          <MultiSelectionFilter
            id="report-type-filter"
            dataOptions={reportTypeOptions}
            name="reportType"
            selectedValues={reportType}
            onChange={handleFilterChange}
          />
        </Accordion>
      )}
    </AccordionSet>
  );
}

Filters.propTypes = {
  activeFilters: PropTypes.object,
  resources: PropTypes.object.isRequired,
  onChangeHandlers: PropTypes.object.isRequired,
  resultOffset: PropTypes.shape({ replace: PropTypes.func.isRequired }),
};

export default Filters;
